import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { tmApi, type TmTagRef, type TmTagView } from "../../api/tm";
import type { ApiError } from "../../api/client";
import { useToast } from "../Toast";

/**
 * Inline tag editor — current tags as chips with × removal, plus a typeahead
 * that suggests existing tags and (on Enter / "Create" link) creates new ones
 * via the upsert endpoint. Caller passes `taskId` for attach/detach and
 * receives `onChange` callbacks after each successful mutation so the parent
 * can refetch the task view (which includes the updated tag list).
 */
export function TmTagPicker({
  taskId, tags, onChange,
}: {
  taskId: string;
  tags: TmTagRef[];
  onChange: () => void | Promise<void>;
}) {
  const { canWrite } = useSectionAccess("tm_tasks");
  const toast = useToast();
  const { t: tr } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [allTags, setAllTags] = useState<TmTagView[]>([]);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    void tmApi.listTags().then(setAllTags).catch(() => setAllTags([]));
  }, []);

  const attachedIds = useMemo(() => new Set(tags.map((t) => t.id)), [tags]);
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allTags
      .filter((t) => !attachedIds.has(t.id))
      .filter((t) => !q || t.name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [allTags, attachedIds, query]);

  const exactMatch = useMemo(
    () => allTags.find((t) => t.name.toLowerCase() === query.trim().toLowerCase()),
    [allTags, query]);
  const canCreate = !!query.trim() && !exactMatch;

  async function attach(tag: TmTagView) {
    if (!canWrite) return;
    setBusy(true);
    try {
      await tmApi.attachTag(taskId, tag.id);
      setQuery("");
      await onChange();
      inputRef.current?.focus();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function createAndAttach() {
    if (!canWrite) return;
    if (!query.trim()) return;
    setBusy(true);
    try {
      const created = await tmApi.createTag({ name: query.trim() });
      setAllTags((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      await tmApi.attachTag(taskId, created.id);
      setQuery("");
      await onChange();
      inputRef.current?.focus();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function detach(tagId: string) {
    if (!canWrite) return;
    setBusy(true);
    try {
      await tmApi.detachTag(taskId, tagId);
      await onChange();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {tags.map((tag) => (
          <span key={tag.id} style={tagChip(tag.color)}>
            {tag.name}
            <button
              type="button"
              onClick={() => void detach(tag.id)}
              disabled={!canWrite || busy}
              aria-label={tr("tm.tags.remove", { defaultValue: "Remove {{name}}", name: tag.name })}
              style={tagX}
            >
              ×
            </button>
          </span>
        ))}
        <div style={{ position: "relative", flex: "0 1 220px" }}>
          <input
            ref={inputRef}
            className="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 120)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (suggestions[0]) void attach(suggestions[0]);
                else if (canCreate) void createAndAttach();
              }
            }}
            placeholder={tr("tm.tags.add", "Add tag…")}
            disabled={!canWrite || busy}
            style={{ height: 28, padding: "0 8px", fontSize: 12, width: "100%" }}
          />
          {focused && (suggestions.length > 0 || canCreate) && (
            <div className="dd-scroll" style={suggestPanel}>
              {suggestions.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void attach(t)}
                  style={suggestRow}
                >
                  <span style={tagChip(t.color, true)}>{t.name}</span>
                </button>
              ))}
              {canCreate && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void createAndAttach()}
                  style={{ ...suggestRow, borderTop: suggestions.length > 0 ? "1px solid var(--border)" : "none" }}
                >
                  <span style={{ color: "var(--ink-3)", fontSize: 12 }}>+ {tr("tm.tags.create", "Create")}</span>
                  <span style={tagChip(null, true)}>{query.trim()}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function tagChip(color: string | null, small = false): React.CSSProperties {
  const bg = color ? `color-mix(in srgb, ${color} 18%, var(--surface))` : "var(--surface-muted)";
  const fg = color ? "var(--ink)" : "var(--ink-2)";
  return {
    display: "inline-flex", alignItems: "center", gap: 4,
    background: bg, color: fg,
    border: `1px solid ${color ?? "var(--border)"}`,
    padding: small ? "2px 8px" : "3px 9px",
    borderRadius: 999,
    fontSize: small ? 11 : 11.5,
    fontWeight: 600,
  };
}

const tagX: React.CSSProperties = {
  background: "transparent", border: "none", color: "inherit",
  cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0,
};

const suggestPanel: React.CSSProperties = {
  position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)",
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: 6, padding: 4, boxShadow: "var(--shadow-2)",
  zIndex: 50, maxHeight: 240, overflowY: "auto",
  display: "flex", flexDirection: "column", gap: 2,
};
const suggestRow: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 8,
  padding: "6px 8px", background: "transparent", border: "none",
  cursor: "pointer", textAlign: "left", borderRadius: 4,
};
